import {Stack, Typography} from '@mui/material';
import React, {Fragment, useEffect} from 'react';
import {Controller, useFormContext} from 'react-hook-form';

import {AlertActions, AlertInfo, AlertModal} from 'presentation/components';
import {ControlledRadioGroup} from 'presentation/components/ControlledRadioGroup';
import {AccordionWithStatus} from 'presentation/pages/private/refund/components/AccordionWithStatus';
import {translations} from 'presentation/translations-new';

import {OperationStateTypesEnum} from 'presentation/constants';
import {RequestErrorModal} from 'presentation/pages/private/refund/components';
import {StatusEnum} from 'presentation/pages/private/refund/constants/accordionByStatus';
import {refundModalityEnum} from 'presentation/pages/private/refund/constants/consultRefundEnum';
import {DocumentEnum} from 'presentation/pages/private/refund/constants/documentEnum';
import {useRefundRequest} from 'presentation/pages/private/refund/hooks';
import {useProviderSearch} from '../../../../hooks/useProviderSearch';
import {useAccordionExpandedStore} from '../../../../store/useAccordionExpandedStore';
import {AccordionExpandedEnum} from '../../../../types';
import {ProviderDataSchema} from '../../validations';
import {CouncilFields, DocumentsFields, FooterForm} from './components';
import {PERSON_TYPE_OPTS, SEARCH_DOCTOR_BY_OPTS} from './constants/formOpts';
import {PersonTypeEnum, SearchDoctorByEnum} from './constants/types';

const {
	title,
	providerType,
	searchDoctor,
	infoTitleConsultation,
	infoTitleOthers,
	unauthorizedProviderModal,
} = translations['pt-br'].privateFlow.refund.receiptRegistrationPage.providerData;

export const ProviderData = () => {
	const accordionExpanded = useAccordionExpandedStore(state => state.accordion);
	const setAccordionExpanded = useAccordionExpandedStore(state => state.setAccordion);
	const {
		control,
		setValue,
		doctorNotFoundByWatch,
		professionalNotFoundByWatch,
		doctorsList,
		doctorsListProfessional,
		handleReset,
		handleSearch,
		IS_LOADING,
		IS_LOADING_PROFESSIONAL,
		personTypeWatch,
		searchDoctorByWatch,
		handleResetDoctorList,
		totalItems,
		totalItemsProfessional,
		handleSeeMore,
		setDoctorsList,
		setDoctorsListProfessional,
		IS_ERROR,
		IS_ERROR_PROFESSIONAL,
		setRequestState,
		setRequestStateProfessional,
		handleCloseUnauthorizedProviderModal,
		unauthorizedProviderModalPrimaryAction,
		unauthorizedProviderModalPrimaryText,
		isProviderUnauthorizedState,
	} = useProviderSearch();

	const {
		formState: {errors},
	} = useFormContext<ProviderDataSchema>();
	const {atoms, formAtoms} = useRefundRequest();

	const isConsultation = atoms.treatmentData.modality === refundModalityEnum.CONSULTA;

	const isConsultationTitle = isConsultation ? infoTitleConsultation : infoTitleOthers;

	const isAccordionExpanded =
		accordionExpanded > AccordionExpandedEnum.PROVIDER_DATA
			? StatusEnum.FINISHED
			: StatusEnum.PENDING;

	const isFinished = isAccordionExpanded === StatusEnum.FINISHED;

	const professionalCpfHasError = !!errors?.professionalCpf;
	const professionalDocumentHasError = !!errors?.professionalDocument;

	const helperTextErrorProfessionalCpf = errors?.professionalCpf?.message;

	const helperTextErrorProfessionalDocument = errors?.professionalDocument?.message;

	const isExpanded = accordionExpanded === AccordionExpandedEnum.PROVIDER_DATA;

	const isAccordionCloseAndFinished = !isExpanded && isFinished;

	const handleEdit = () => {
		if (isAccordionCloseAndFinished) {
			setAccordionExpanded(AccordionExpandedEnum.PROVIDER_DATA);
		}
	};

	useEffect(() => {
		if (formAtoms && formAtoms?.providerDataForm?.professionalDocument) {
			handleSearch();
			if (formAtoms?.providerDataForm?.searchDoctorBy) {
				setValue('councilAcronym', formAtoms.providerDataForm.councilAcronym);
				setValue('councilNumber', formAtoms.providerDataForm.councilNumber);
				setValue('councilState', formAtoms.providerDataForm.councilState);
				setValue('doctorNotFound', formAtoms.providerDataForm.doctorNotFound);
				setValue('professionalCode', formAtoms.providerDataForm.professionalCode);
				setValue('professionalCpf', formAtoms.providerDataForm.professionalCpf);
				setValue('professionalDocument', formAtoms.providerDataForm.professionalDocument);
				setValue('professionalName', formAtoms.providerDataForm.professionalName);
				setValue('professionalNotFound', formAtoms.providerDataForm.professionalNotFound);
				setValue('professionalType', formAtoms.providerDataForm.professionalType);
				setValue('providerCode', formAtoms.providerDataForm.providerCode);
				setValue(
					'providerDocumentMatchUserCpf',
					formAtoms.providerDataForm.providerDocumentMatchUserCpf,
				);
				setValue('providerName', formAtoms.providerDataForm.providerName);
				setValue('providerUnauthorized', formAtoms.providerDataForm.providerUnauthorized);
				setValue('searchDoctorBy', formAtoms.providerDataForm.searchDoctorBy);
				handleSearch(true);
			}
		}
	}, []);

	return (
		<AccordionWithStatus
			expanded={isExpanded}
			onClick={handleEdit}
			title={title}
			removeIcon
			cursor={isAccordionCloseAndFinished ? 'pointer' : 'default'}
			status={isAccordionExpanded}>
			<Stack gap={2}>
				<AlertInfo.Root severity="info">
					<AlertInfo.Title sx={{fontWeight: 400, mb: 0}} title={isConsultationTitle} />
				</AlertInfo.Root>
				<Stack gap={1}>
					<Typography color="primary" fontWeight={600}>
						{providerType}
					</Typography>

					<Controller
						control={control}
						name="professionalType"
						render={({field: {value, onChange}}) => (
							<ControlledRadioGroup
								gap={'16px'}
								direction="row"
								radioOpts={PERSON_TYPE_OPTS}
								key={value}
								value={value}
								onChange={e => {
									onChange(e);
									handleReset();
								}}
							/>
						)}
					/>
				</Stack>
				{personTypeWatch === PersonTypeEnum.PHYSICAL_PERSON ? (
					<Fragment>
						<Stack gap={1}>
							<Typography color="primary" fontWeight={600}>
								{searchDoctor}
							</Typography>

							<Controller
								control={control}
								name="searchDoctorBy"
								render={({field: {value, onChange}}) => (
									<ControlledRadioGroup
										gap="16px"
										direction="row"
										radioOpts={SEARCH_DOCTOR_BY_OPTS}
										key={value}
										value={value}
										onChange={e => {
											onChange(e);
											handleReset();
										}}
									/>
								)}
							/>
						</Stack>
						<Stack gap={1}>
							{searchDoctorByWatch === SearchDoctorByEnum.DOCUMENT ? (
								<DocumentsFields
									documentName={DocumentEnum.CPF}
									customRegister={'professionalCpf'}
									handleReset={handleReset}
									error={professionalCpfHasError}
									helperText={
										professionalCpfHasError
											? helperTextErrorProfessionalCpf
											: undefined
									}
								/>
							) : (
								<CouncilFields handleReset={handleResetDoctorList} />
							)}
							<FooterForm
								setDoctorList={setDoctorsList}
								totalItems={totalItems}
								documentType={DocumentEnum.CPF}
								doctorList={doctorsList}
								doctorNotFound={doctorNotFoundByWatch}
								searchDoctorByWatch={searchDoctorByWatch}
								isLoading={IS_LOADING}
								onSubmit={handleSearch}
								personType={personTypeWatch}
								seeMore={handleSeeMore}
							/>
						</Stack>
					</Fragment>
				) : (
					<Fragment>
						<DocumentsFields
							customRegister={'professionalDocument'}
							documentName={DocumentEnum.CNPJ}
							handleReset={handleReset}
							error={professionalDocumentHasError}
							helperText={
								professionalDocumentHasError
									? helperTextErrorProfessionalDocument
									: undefined
							}
						/>
						<FooterForm
							setDoctorList={setDoctorsList}
							totalItems={totalItems}
							totalItemsProfessional={totalItemsProfessional}
							documentType={DocumentEnum.CNPJ}
							doctorList={doctorsList}
							doctorsListProfessional={doctorsListProfessional}
							doctorNotFound={doctorNotFoundByWatch}
							professionalNotFound={professionalNotFoundByWatch}
							searchDoctorByWatch={searchDoctorByWatch}
							isLoading={IS_LOADING}
							isLoadingProfessional={IS_LOADING_PROFESSIONAL}
							onSubmit={handleSearch}
							personType={personTypeWatch}
							seeMore={handleSeeMore}
							onReset={handleReset}
							onResetProfessionalList={handleResetDoctorList}
							setDoctorsListProfessional={setDoctorsListProfessional}
						/>
					</Fragment>
				)}
			</Stack>

			<RequestErrorModal
				open={IS_ERROR || IS_ERROR_PROFESSIONAL}
				setOpen={() => {
					setRequestState(OperationStateTypesEnum.NONE);
					setRequestStateProfessional(OperationStateTypesEnum.NONE);
				}}
			/>

			<AlertModal
				open={isProviderUnauthorizedState}
				onClose={handleCloseUnauthorizedProviderModal}
				variant="info"
				headerTitle={unauthorizedProviderModal.headerTitle}
				title={unauthorizedProviderModal.title}
				description={unauthorizedProviderModal.description}
				footerContent={
					<AlertActions
						primaryText={unauthorizedProviderModalPrimaryText}
						primaryAction={unauthorizedProviderModalPrimaryAction}
						secondaryText={unauthorizedProviderModal.secondaryActionText}
						secondaryAction={handleCloseUnauthorizedProviderModal}
						flexDirection="column"
					/>
				}
			/>
		</AccordionWithStatus>
	);
};
